/* ResetLab — Nervous system & lymph reset toolkit.
 * Guided micro-sessions with countdown timers per step + optional voice cue. */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Play, Pause, SkipForward, X, Flame, Volume2, VolumeX, Send, Sun, Sunrise, Moon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOOD_MAP = {
  stressed: { label: 'Stressad', emoji: '😰' },
  foggy:    { label: 'Trött / dimmig', emoji: '☁️' },
  low:      { label: 'Nedstämd', emoji: '💧' },
  wired:    { label: 'Uppvarvad', emoji: '⚡' },
};

const AFTER_MOODS = [
  { id: 'lugnare', label: 'Lugnare 💜' },
  { id: 'piggare', label: 'Piggare ✨' },
  { id: 'samma',   label: 'Ungefär samma' },
  { id: 'trött',   label: 'Lite trött' },
];

function speak(text, langHint = 'sv-SE') {
  try {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = langHint;
    u.rate = 0.92;
    u.pitch = 1.05;
    window.speechSynthesis.speak(u);
  } catch { /* silent */ }
}

export default function ResetLab() {
  const [data, setData] = useState({ sessions: [], categories: [] });
  const [history, setHistory] = useState({ streak: 0, total: 0, per_category: {}, recent: [] });
  const [suggestions, setSuggestions] = useState([]);
  const [selectedMood, setSelectedMood] = useState(null);
  const [activeSession, setActiveSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [voiceOn, setVoiceOn] = useState(true);
  const [category, setCategory] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: s }, { data: h }, { data: sg }] = await Promise.all([
        axios.get(`${API}/reset-lab/sessions`),
        axios.get(`${API}/reset-lab/history?days=14`, { withCredentials: true }),
        axios.get(`${API}/reset-lab/suggest`, { withCredentials: true }),
      ]);
      setData(s);
      setHistory(h);
      setSuggestions(sg.suggestions || []);
    } catch { toast.error('Kunde inte ladda reset-verkstan'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const pickMood = async (moodId) => {
    setSelectedMood(moodId);
    try {
      const { data: sg } = await axios.get(`${API}/reset-lab/suggest?mood=${moodId}`, { withCredentials: true });
      setSuggestions(sg.suggestions || []);
    } catch { /* silent */ }
  };

  const shownSessions = category === 'all'
    ? data.sessions
    : data.sessions.filter((s) => s.category === category);

  if (loading) {
    return (
      <div className="text-center py-12 text-slate-400">
        <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Laddar…
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-4" data-testid="reset-lab-page">
      {/* Hero */}
      <div className="rounded-3xl p-5 border relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #F0F4FF 0%, #F5EEFF 50%, #FFF0F5 100%)', borderColor: '#DDD6FE' }}>
        <div className="absolute -right-4 -top-4 opacity-15 text-7xl leading-none pointer-events-none">🌀</div>
        <div className="text-[11px] uppercase font-semibold tracking-widest text-violet-800/70">Reset-verkstan</div>
        <h1 className="text-2xl font-bold text-violet-950" style={{ fontFamily: '"Caveat", ui-serif, Georgia, serif' }}>
          Starta om nerv- & lymfsystemet 🌊
        </h1>
        <p className="text-sm text-violet-900/80 mt-1">
          Korta guidade sessioner (2-8 min) med timers. Perfekt när det tar över.
        </p>

        <div className="flex items-center gap-3 mt-3 flex-wrap">
          {history.streak > 0 && (
            <span className="text-[11px] rounded-full px-2 py-0.5 border bg-white/70 border-amber-200 text-amber-800 inline-flex items-center gap-1">
              <Flame className="w-3 h-3" /> {history.streak} dagar streak
            </span>
          )}
          <span className="text-[11px] text-violet-800/60">{history.total} sessioner senaste 14 dagar</span>
          <div className="flex-1" />
          <button
            onClick={() => setVoiceOn((v) => !v)}
            data-testid="reset-lab-voice-toggle"
            className={`text-[11px] px-2 py-1 rounded-full border inline-flex items-center gap-1 ${voiceOn ? 'bg-emerald-100 border-emerald-300 text-emerald-800' : 'bg-slate-100 border-slate-200 text-slate-600'}`}
            title={voiceOn ? 'Röst av' : 'Röst på'}
          >
            {voiceOn ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />}
            Röst {voiceOn ? 'på' : 'av'}
          </button>
        </div>
      </div>

      {/* Mood picker */}
      <div className="rounded-2xl bg-white border border-slate-100 p-4" data-testid="reset-lab-mood">
        <div className="text-sm font-semibold text-slate-800 mb-2">Hur känns det just nu?</div>
        <div className="flex flex-wrap gap-1.5">
          {Object.entries(MOOD_MAP).map(([id, m]) => (
            <button
              key={id}
              onClick={() => pickMood(id)}
              data-testid={`reset-lab-mood-${id}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border-2 transition ${
                selectedMood === id
                  ? 'bg-violet-500 border-violet-500 text-white'
                  : 'bg-white border-slate-200 text-slate-700 hover:border-violet-400'
              }`}
            >
              {m.emoji} {m.label}
            </button>
          ))}
        </div>
      </div>

      {/* Suggestions */}
      {suggestions.length > 0 && (
        <div className="space-y-2" data-testid="reset-lab-suggestions">
          <div className="text-xs uppercase font-semibold tracking-wider text-violet-700/70 pl-1">
            {selectedMood ? `Föreslaget för ${MOOD_MAP[selectedMood]?.label.toLowerCase()}` : 'Prova något av dessa'}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {suggestions.slice(0, 4).map((s) => (
              <SessionCard key={s.id} session={s} onOpen={() => setActiveSession(s)} />
            ))}
          </div>
        </div>
      )}

      {/* Category filter */}
      <div className="flex flex-wrap gap-1.5 pt-2" data-testid="reset-lab-categories">
        <button
          onClick={() => setCategory('all')}
          className={`text-xs px-2.5 py-1 rounded-full border ${category === 'all' ? 'bg-violet-500 border-violet-500 text-white' : 'bg-white border-slate-200 text-slate-600'}`}
        >
          Alla ({data.sessions.length})
        </button>
        {data.categories.map((c) => (
          <button
            key={c.id}
            onClick={() => setCategory(c.id)}
            data-testid={`reset-lab-cat-${c.id}`}
            className={`text-xs px-2.5 py-1 rounded-full border ${category === c.id ? 'bg-violet-500 border-violet-500 text-white' : 'bg-white border-slate-200 text-slate-600'}`}
          >
            {c.emoji} {c.label}
          </button>
        ))}
      </div>

      {/* All sessions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {shownSessions.map((s) => (
          <SessionCard key={s.id} session={s} onOpen={() => setActiveSession(s)} />
        ))}
      </div>

      {/* Session player modal */}
      {activeSession && (
        <SessionPlayer
          session={activeSession}
          voiceOn={voiceOn}
          onClose={() => { setActiveSession(null); load(); }}
        />
      )}
    </div>
  );
}

function SessionCard({ session, onOpen }) {
  const [pushingTime, setPushingTime] = useState(null);
  const [pushed, setPushed] = useState(false);

  const push = async (habit_time, e) => {
    e.stopPropagation();
    setPushingTime(habit_time);
    try {
      await axios.post(`${API}/reset-lab/push-to-flow`,
        { session_id: session.id, habit_time, make_habit: false },
        { withCredentials: true });
      setPushed(true);
      toast.success(`Skickat till Idag-lista 💜`);
      setTimeout(() => setPushed(false), 3000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Kunde inte skicka');
    } finally { setPushingTime(null); }
  };

  return (
    <div
      className={`rounded-2xl p-4 border bg-gradient-to-br ${session.gradient} border-slate-200 hover:shadow-md transition`}
      data-testid={`reset-lab-card-${session.id}`}
    >
      <button
        type="button"
        onClick={onOpen}
        data-testid={`reset-lab-open-${session.id}`}
        className="text-left w-full hover:scale-[1.01] transition"
      >
        <div className="flex items-start justify-between">
          <div className="text-3xl leading-none">{session.emoji}</div>
          <span className="text-[10px] uppercase font-bold tracking-wider text-slate-600 bg-white/60 rounded-full px-2 py-0.5">
            {session.total_min} min
          </span>
        </div>
        <div className="mt-2 text-base font-bold text-slate-900" style={{ fontFamily: 'ui-serif, Georgia, serif' }}>
          {session.title}
        </div>
        <p className="text-[11px] text-slate-700/80 mt-1 line-clamp-2">{session.why}</p>
      </button>
      {/* Push to Idag-lista */}
      <div className="mt-2 pt-2 border-t border-white/40 flex items-center gap-1.5 flex-wrap">
        <span className="text-[10px] uppercase font-semibold tracking-wider text-slate-600/70 mr-1">
          <Send className="w-2.5 h-2.5 inline" /> Idag-lista
        </span>
        {[
          { id: 'morning', label: 'Morgon', icon: Sunrise },
          { id: 'lunch',   label: 'Lunch',  icon: Sun },
          { id: 'evening', label: 'Kväll',  icon: Moon },
        ].map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={(e) => push(t.id, e)}
              disabled={pushingTime !== null}
              data-testid={`reset-lab-push-${session.id}-${t.id}`}
              className={`text-[10px] px-2 py-0.5 rounded-full border transition inline-flex items-center gap-0.5 ${
                pushed ? 'bg-emerald-500 border-emerald-500 text-white' :
                pushingTime === t.id ? 'bg-slate-200 border-slate-300 text-slate-600' :
                'bg-white/70 border-slate-300 text-slate-700 hover:bg-white'
              }`}
            >
              {pushingTime === t.id ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Icon className="w-2.5 h-2.5" />}
              {t.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SessionPlayer({ session, voiceOn, onClose }) {
  const [stepIdx, setStepIdx] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(session.steps[0]?.seconds || 30);
  const [running, setRunning] = useState(true);
  const [done, setDone] = useState(false);
  const [moodAfter, setMoodAfter] = useState(null);
  const [saving, setSaving] = useState(false);
  const intervalRef = useRef(null);
  const startedAt = useRef(Date.now());

  const step = session.steps[stepIdx];

  useEffect(() => {
    if (voiceOn && step) {
      speak(`${step.title}. ${step.instruction}`);
    }
    return () => { try { window.speechSynthesis && window.speechSynthesis.cancel(); } catch { /* silent */ } };
  }, [stepIdx, step, voiceOn]);

  useEffect(() => {
    if (!running || done) return;
    intervalRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(intervalRef.current);
          if (stepIdx < session.steps.length - 1) {
            setStepIdx((i) => i + 1);
            return session.steps[stepIdx + 1].seconds;
          }
          setDone(true);
          setRunning(false);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [running, done, stepIdx, session.steps]);

  const skip = () => {
    clearInterval(intervalRef.current);
    if (stepIdx < session.steps.length - 1) {
      setStepIdx((i) => i + 1);
      setSecondsLeft(session.steps[stepIdx + 1].seconds);
    } else {
      setDone(true);
      setRunning(false);
    }
  };

  const finish = async () => {
    setSaving(true);
    try {
      const actualMin = Math.max(1, Math.round((Date.now() - startedAt.current) / 60000));
      await axios.post(`${API}/reset-lab/complete`, {
        session_id: session.id,
        minutes_actual: actualMin,
        mood_after: moodAfter,
      }, { withCredentials: true });
      toast.success('Sparat 💜');
      onClose();
    } catch { toast.error('Kunde inte spara'); onClose(); }
    finally { setSaving(false); }
  };

  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, '0');
  const ss = String(secondsLeft % 60).padStart(2, '0');
  const totalSteps = session.steps.length;
  const overallPct = ((stepIdx + (1 - secondsLeft / (step?.seconds || 1))) / totalSteps) * 100;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="reset-lab-player"
    >
      <div
        className={`relative rounded-3xl p-6 max-w-md w-full shadow-2xl border bg-gradient-to-br ${session.gradient} border-slate-200`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-full bg-white/60 hover:bg-white text-slate-500"
          aria-label="Stäng"
          data-testid="reset-lab-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="text-center mb-4">
          <div className="text-4xl">{session.emoji}</div>
          <h2 className="text-xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'ui-serif, Georgia, serif' }}>
            {session.title}
          </h2>
          <div className="text-[10px] uppercase tracking-widest text-slate-600 mt-1">
            Steg {stepIdx + 1} / {totalSteps}
          </div>
        </div>

        {!done ? (
          <>
            <div className="rounded-2xl bg-white/70 backdrop-blur p-4 mb-4">
              <div className="text-base font-bold text-slate-900">{step.title}</div>
              <p className="text-sm text-slate-700 mt-1">{step.instruction}</p>
            </div>

            <div className="text-center mb-3">
              <div
                className="text-5xl font-bold text-slate-900 tabular-nums"
                style={{ fontFamily: 'ui-monospace, monospace' }}
                data-testid="reset-lab-timer"
              >
                {mm}:{ss}
              </div>
            </div>

            <div className="w-full h-1.5 bg-white/50 rounded-full overflow-hidden mb-3">
              <div className="h-full bg-slate-800/70" style={{ width: `${overallPct}%`, transition: 'width 0.9s linear' }} />
            </div>

            <div className="flex justify-center gap-2">
              <button
                onClick={() => setRunning((r) => !r)}
                data-testid="reset-lab-play-pause"
                className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-white/80 hover:bg-white text-slate-800 text-sm font-semibold shadow"
              >
                {running ? <><Pause className="w-4 h-4" /> Pausa</> : <><Play className="w-4 h-4" /> Fortsätt</>}
              </button>
              <button
                onClick={skip}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/60 hover:bg-white text-slate-700 text-sm"
                aria-label="Nästa"
                data-testid="reset-lab-skip"
              >
                <SkipForward className="w-4 h-4" />
              </button>
            </div>
          </>
        ) : (
          <div className="text-center space-y-3" data-testid="reset-lab-finish">
            <div className="text-3xl">✨</div>
            <div className="text-xl font-bold text-slate-900" style={{ fontFamily: 'ui-serif, Georgia, serif' }}>
              Klar! Hur känns det nu?
            </div>
            <div className="flex flex-wrap justify-center gap-1.5">
              {AFTER_MOODS.map((m) => (
                <button
                  key={m.id}
                  onClick={() => setMoodAfter(m.id)}
                  data-testid={`reset-lab-mood-after-${m.id}`}
                  className={`text-xs px-3 py-1 rounded-full border-2 ${
                    moodAfter === m.id ? 'bg-slate-900 border-slate-900 text-white' : 'bg-white border-slate-200 text-slate-700'
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
            <button
              onClick={finish}
              disabled={saving}
              data-testid="reset-lab-finish-save"
              className="mt-2 inline-flex items-center gap-1.5 px-6 py-2 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white text-sm font-semibold disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <span>Spara & stäng 💜</span>}
            </button>
            <div className="pt-2 border-t border-white/40 mt-2">
              <div className="text-[10px] uppercase font-semibold tracking-wider text-slate-600 mb-1.5">
                Vill du göra detta till en rutin?
              </div>
              <div className="flex flex-wrap justify-center gap-1.5">
                {[
                  { id: 'morning', label: '🌅 Morgon-rutin' },
                  { id: 'evening', label: '🌙 Kvälls-rutin' },
                ].map((t) => (
                  <button
                    key={t.id}
                    data-testid={`reset-lab-finish-push-${t.id}`}
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/reset-lab/push-to-flow`,
                          { session_id: session.id, habit_time: t.id, make_habit: true },
                          { withCredentials: true });
                        toast.success('Nu i din Idag-lista som vana 💜');
                      } catch { toast.error('Kunde inte spara vanan'); }
                    }}
                    className="text-xs px-3 py-1 rounded-full bg-white/70 hover:bg-white border-2 border-slate-300 text-slate-800"
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
